import axiosInstance from './axios';
import { toast } from 'react-toastify';

/**
 * Build download URL for a contribution attachment.
 */
export function buildContributionAttachmentUrl(contributionId, filename) {
  return `/api/contributions/download/${encodeURIComponent(contributionId)}/${encodeURIComponent(filename)}`;
}

/**
 * Fetch a contribution attachment as a Blob.
 */
export async function fetchContributionAttachmentBlob(contributionId, filename) {
  const url = buildContributionAttachmentUrl(contributionId, filename);
  const res = await axiosInstance.get(url, { responseType: 'blob' });
  return res.data; // Blob
}

/**
 * Create an object URL for previewing an attachment (remember to revoke when done).
 */
export async function getContributionAttachmentObjectUrl(contributionId, attachment) {
  if (!attachment?.filename) throw new Error('Attachment filename is required');
  const blob = await fetchContributionAttachmentBlob(contributionId, attachment.filename);
  return URL.createObjectURL(blob);
}

/**
 * Download an attachment using the original name when available.
 * Handles toasts and errors internally for a simple callsite.
 */
export async function downloadContributionAttachment(contributionId, attachment) {
  try {
    if (!attachment?.filename) {
      toast.error('Attachment filename is missing');
      return;
    }

    const blob = await fetchContributionAttachmentBlob(contributionId, attachment.filename);

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = attachment.originalname || attachment.filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (err) {
    let message = 'Download failed';
    try {
      if (err?.response?.status && err?.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        message = `Download failed (${err.response.status})${text ? `: ${text}` : ''}`;
      }
    } catch (_) {}
    toast.error(message);
    throw err;
  }
}

/**
 * Create an object URL for a communication (chat) message attachment.
 */
export async function getCommunicationAttachmentObjectUrl(messageId, attachment) {
  if (!attachment?.filename) throw new Error('Attachment filename is required');
  const url = `/api/communications/download/${encodeURIComponent(messageId)}/${encodeURIComponent(attachment.filename)}`;
  const res = await axiosInstance.get(url, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
}

/**
 * Download a communication (chat) message attachment using the original name when available.
 */
export async function downloadCommunicationAttachment(messageId, attachment) {
  try {
    if (!attachment?.filename) {
      toast.error('Attachment filename is missing');
      return;
    }

    const url = `/api/communications/download/${encodeURIComponent(messageId)}/${encodeURIComponent(attachment.filename)}`;
    const res = await axiosInstance.get(url, { responseType: 'blob' });
    const objectUrl = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = attachment.originalname || attachment.filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (err) {
    let message = 'Download failed';
    try {
      if (err?.response?.status && err?.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        message = `Download failed (${err.response.status})${text ? `: ${text}` : ''}`;
      }
    } catch (_) {}
    toast.error(message);
    throw err;
  }
}
